import { Component, OnInit } from '@angular/core';
import * as Highcharts from 'highcharts';
import { interval, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { JobStatus } from 'src/app/common';
import { Utility } from 'src/app/core/common';
import { CalendarShortKey, PersianDate } from 'src/app/core/persian-date';
import { MonitoringService } from 'src/app/services/monitoring.service';
import { SharedService } from 'src/app/services/shared.service';
import { UserService } from 'src/app/services/user.service';

@Component({
  selector: 'admin-kpi-dashboard',
  templateUrl: './kpi-dashboard.component.html',
  styleUrls: ['./kpi-dashboard.component.scss']
})
export class KpiDashboardComponent implements OnInit {
  Highcharts = Highcharts;
  operators: any[];
  showAll = false;

  constructor(private monitoringService: MonitoringService, private sharedService: SharedService, private userService: UserService) {
    (interval(10 * 60 * 1000)).pipe(takeUntil(this.unsubscribe$)).subscribe(() => this.intervalReports());
    this.userService.karpashas$.subscribe(_ => this.operators = _);
  }

  async ngOnInit() {
    if (!this.sharedService.isBrowser) return;
    this.actions = await this.monitoringService.actions();
    await this.intervalReports();
    await this.getTimeReport();
  }

  private unsubscribe$ = new Subject<void>();
  private async intervalReports() {
    await this.getKpiReport();
    await this.getPendingActivitiesReport();
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  actions: any[];

  data: any;
  pendingActivities: any;
  pendingSummary: any;
  intervals = [
    { base: PersianDate.DateRanges.last3hours, compare: PersianDate.DateRanges.last3hours_lastweek },
    { base: PersianDate.DateRanges.last24hours, compare: PersianDate.DateRanges.last24hours_lastweek },
    { base: PersianDate.DateRanges.last7daystonow, compare: PersianDate.DateRanges.last14to7days },
    { base: PersianDate.DateRanges.last30daystonow, compare: PersianDate.DateRanges.last60to30days },
  ];

  get allActions() {
    return Utility.flat(this.actions.map(_ => _.key).filter((v, i, a) => a.indexOf(v) == i).map(_ => _.split(',')));
  }

  private async getKpiReport() {
    this.data = await this.monitoringService.kpi_vkSeeker(
      this.allActions,
      Utility.flat(this.intervals.map(_ => [_.base, _.compare])).map(_ => {
        return { Key: _.key, From: _.date, To: _.to };
      }));
  }

  private async getPendingActivitiesReport() {
    this.pendingActivities = await this.monitoringService.pendingActivities("now-7d");
    this.pendingSummary = {
      paidPendingJobs: this.pendingActivities.jobStats?.paid?.reduce((acc, o) => acc + ([JobStatus.Started, JobStatus.Rejected, JobStatus.Paused].includes(+(o.Key)) ? 0 : o.DocCount), 0),
      notPaidPendingJobs: this.pendingActivities.jobStats?.free?.reduce((acc, o) => acc + ([JobStatus.Started, JobStatus.Rejected, JobStatus.Paused].includes(+(o.Key)) ? 0 : o.DocCount), 0),
      warmLeads: this.pendingActivities.seekerLeadStats['3'],
      lukeWarmLeads: this.pendingActivities.seekerLeadStats['2'],
      tasks: this.pendingActivities.overdueTasksStats?.reduce((acc, o) => acc + o.DocCount, 0)
    };
    this.pendingActivities.overdueTasksStats?.forEach(_ => _.Operator = this.operators.find(o => o.Id == _.Key)?.Name);
  }

  _data(interval: CalendarShortKey, action: any) {
    if (!this.data[interval.key]) return 0;

    switch (action.key) {
      case 'paid-seeker-rate':
        if (this.data[interval.key]?.['new-seeker']?.users > 0) {
          return Math.floor((
            (this.data[interval.key]?.['vanguard-paid']?.value || 0) +
            (this.data[interval.key]?.['karpasha-paid']?.value || 0) +
            (this.data[interval.key]?.['readytowork-paid']?.value || 0) +
            (this.data[interval.key]?.['agent-registration-paid']?.value || 0)
          ) / this.data[interval.key]?.['new-seeker'].users);
        }
        return 0;
      case 'conversion-seeker-rate':
        if (this.data[interval.key]?.['new-seeker']?.users > 0) {
          return Math.ceil((
            (this.data[interval.key]?.['vanguard-paid']?.users || 0) +
            (this.data[interval.key]?.['karpasha-paid']?.users || 0) +
            (this.data[interval.key]?.['agent-registration-paid']?.users || 0)
          ) * 100 / this.data[interval.key]?.['new-seeker'].users * 100) / 100;
        }
        return 0;
      default:
        var result = 0;
        action.key.split(',').forEach(_ => result += this.data[interval.key]?.[_]?.[action.result] || 0);
        return result;
    }
  }
  _data_tooltip(interval: CalendarShortKey, action: any) {
    if (!this.data[interval.key]) return null;

    switch (action.key) {
      case 'paid-seeker-rate':
      case 'conversion-seeker-rate':
        return null;
      default:
        return action.key.split(',').map(_ => `${_} : ${this.data[interval.key]?.[_]?.[action.result] || 0}`).join('\n');
    }
  }

  _diff(interval: any, action: any) {
    return this._data(interval.base, action) - this._data(interval.compare, action);
  }
  _conversion(interval: any, action: any) {
    var current = this._data(interval, action);
    var previous = this._data(interval, { key: action.conversion, result: action.result });
    return current / previous;
  }

  reportFrom: Date;
  reportTo: Date;
  interval = 'Day';
  options: Highcharts.Options;

  async getTimeReport() {
    var data = (await this.monitoringService.actionReport(
      this.reportFrom,
      this.reportTo,
      this.allActions,
      this.interval)
    )?.reverse();
    this.options = {
      chart: {
        // type: 'column',
        backgroundColor: 'transparent',
        width: null
      },
      title: null,
      xAxis: {
        categories: data.map(_ => {
          switch (this.interval) {
            case 'Week':
            case 'Month':
            case 'Year':
              return `${PersianDate.toString(new Date(_.from))} - ${PersianDate.toString(new Date(_.to))}`;
            case 'Hour':
              return new PersianDate(new Date(_.from)).toString('[w] [mm]/[dd] [hh]:00');
            default:
              return new PersianDate(new Date(_.from)).toString('[w] [mm]/[dd]');
          }
        }),
      },
      yAxis: [{
        min: 0,
        minTickInterval: 1,
        title: { text: 'تعداد' },
      }, {
        min: 0,
        minTickInterval: 1,
        title: { text: 'مبلغ' },
        opposite: true
      }, {
        min: 0,
        minTickInterval: 1,
        title: { text: '' },
        opposite: true
      }, {
        min: 0,
        title: { text: 'درصد' },
        opposite: true
      }],
      tooltip: {
        useHTML: true,
        shared: true,
        headerFormat: `
          <div style="text-align:right;direction:rtl;font-weight:bold">{point.key}</div>
          <table style="direction:rtl;text-align:right;float:right">
        `,
        pointFormat: `
          <tr style="direction:rtl;text-align:right">
            <td style="font-weight:bold;font-size:12px;text-align: right;color: {series.color}">{series.name}: </td>
            <td style="text-align: right;direction:ltr"><b>{point.y}</b></td>
          </tr>
        `,
        footerFormat: `
        </table>
        `
      },
      // legend: { enabled: false },
      // plotOptions: { series: { stacking: 'normal' } },
      series: this.actions.map(a => {
        return {
          type: 'line',
          name: a.title,
          yAxis: a.yAxis != undefined ? a.yAxis : (a.result == 'value' ? 1 : 0),
          visible: a.visible != false,
          data: data.map(_ => {
            switch (a.key) {
              case 'paid-seeker-rate':
                if (_.actions['new-seeker']?.users > 0) {
                  return Math.floor((
                    (_.actions['vanguard-paid']?.value || 0) +
                    (_.actions['karpasha-paid']?.value || 0) +
                    (_.actions['readytowork-paid']?.value || 0) +
                    (_.actions['agent-registration-paid']?.value || 0)
                  ) / _.actions['new-seeker'].users);
                }
                return 0;
              case 'conversion-seeker-rate':
                if (_.actions['new-seeker']?.users > 0) {
                  return Math.ceil((
                    (_.actions['vanguard-paid']?.users || 0) +
                    (_.actions['karpasha-paid']?.users || 0) +
                    (_.actions['agent-registration-paid']?.users || 0)
                  ) * 100 / _.actions['new-seeker'].users * 100) / 100;
                }
                return 0;
              default:
                var result = 0;
                a.key.split(',').forEach(key => result += _.actions[key]?.[a.result] || 0);
                return result;
            }
          })
        };
      })
    };
  }

}
